/**
 * Device Capability Detection
 *
 * Detects device hardware capabilities to determine if cry detection
 * should be automatically enabled and at what inference rate.
 */

export type DeviceTier = "high" | "medium" | "low";

export interface DeviceCapability {
  tier: DeviceTier;
  cryDetectionRecommended: boolean;
  inferenceIntervalMs: number;
  motionDetectionRecommended: boolean;
  motionProcessingIntervalMs: number;
  cpuCores: number;
  memoryGB: number | null;
  gpuTier: "high" | "medium" | "low" | "unknown";
  batteryLevel: number | null;
  isCharging: boolean | null;
  benchmarkScore: number;
}

interface NavigatorWithMemory extends Navigator {
  deviceMemory?: number;
}

interface BatteryManager {
  charging: boolean;
  level: number;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

interface NavigatorWithBattery extends Navigator {
  getBattery?: () => Promise<BatteryManager>;
}

// Cache the result to avoid re-computation
let cachedCapability: DeviceCapability | null = null;

/**
 * Detect device capabilities for cry detection optimization
 */
export async function detectDeviceCapability(): Promise<DeviceCapability> {
  if (cachedCapability) {
    return cachedCapability;
  }

  const [gpuInfo, batteryInfo, benchmarkScore] = await Promise.all([
    detectGPUTier(),
    detectBatteryStatus(),
    runQuickBenchmark(),
  ]);

  const cpuCores = navigator.hardwareConcurrency || 2;
  const memoryGB = (navigator as NavigatorWithMemory).deviceMemory ?? null;

  // Calculate device tier based on hardware
  const tier = calculateDeviceTier({
    cpuCores,
    memoryGB,
    gpuTier: gpuInfo.tier,
    benchmarkScore,
  });

  // Determine inference interval based on tier
  const inferenceIntervalMs = getInferenceInterval(tier);

  // Determine motion processing interval based on tier
  const motionProcessingIntervalMs = getMotionProcessingInterval(tier);

  // Recommend cry detection for medium+ devices, or low devices that are charging
  const cryDetectionRecommended =
    tier === "high" ||
    tier === "medium" ||
    (tier === "low" && batteryInfo.isCharging === true);

  // Motion detection is more compute-intensive (video processing)
  // Only recommend for high tier, or medium tier when charging
  // Low tier devices should have it off by default
  const motionDetectionRecommended =
    tier === "high" || (tier === "medium" && batteryInfo.isCharging !== false);

  cachedCapability = {
    tier,
    cryDetectionRecommended,
    inferenceIntervalMs,
    motionDetectionRecommended,
    motionProcessingIntervalMs,
    cpuCores,
    memoryGB,
    gpuTier: gpuInfo.tier,
    batteryLevel: batteryInfo.level,
    isCharging: batteryInfo.isCharging,
    benchmarkScore,
  };

  console.log("[DeviceCapability] Detected:", cachedCapability);
  return cachedCapability;
}

/**
 * Get cached capability (synchronous, returns null if not yet detected)
 */
export function getCachedCapability(): DeviceCapability | null {
  return cachedCapability;
}

/**
 * Clear cached capability (useful for re-detection)
 */
export function clearCapabilityCache(): void {
  cachedCapability = null;
}

/**
 * Detect GPU capabilities using WebGL
 */
async function detectGPUTier(): Promise<{
  tier: DeviceCapability["gpuTier"];
  renderer: string;
}> {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");

    if (!gl) {
      return { tier: "unknown", renderer: "none" };
    }

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) {
      return { tier: "medium", renderer: "unknown" };
    }

    const renderer = gl.getParameter(
      debugInfo.UNMASKED_RENDERER_WEBGL
    ) as string;
    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string;

    // High-tier GPU patterns
    const highTierPatterns = [
      /Apple GPU/i, // Apple Silicon
      /Apple M[1-9]/i, // Apple M-series
      /Adreno 6[0-9][0-9]/i, // High-end Qualcomm
      /Adreno 7[0-9][0-9]/i, // Latest Qualcomm
      /Mali-G7[0-9]/i, // High-end ARM
      /Mali-G[8-9][0-9]/i, // Latest ARM
      /NVIDIA/i, // Desktop NVIDIA
      /AMD Radeon/i, // Desktop AMD
    ];

    // Medium-tier GPU patterns
    const mediumTierPatterns = [
      /Adreno 5[0-9][0-9]/i, // Mid-range Qualcomm
      /Mali-G5[0-9]/i, // Mid-range ARM
      /Mali-G6[0-9]/i, // Mid-range ARM
      /Intel/i, // Intel integrated
    ];

    const fullRenderer = `${vendor} ${renderer}`;

    if (highTierPatterns.some((pattern) => pattern.test(fullRenderer))) {
      return { tier: "high", renderer: fullRenderer };
    }

    if (mediumTierPatterns.some((pattern) => pattern.test(fullRenderer))) {
      return { tier: "medium", renderer: fullRenderer };
    }

    return { tier: "low", renderer: fullRenderer };
  } catch {
    return { tier: "unknown", renderer: "error" };
  }
}

/**
 * Detect battery status
 */
async function detectBatteryStatus(): Promise<{
  level: number | null;
  isCharging: boolean | null;
}> {
  try {
    const nav = navigator as NavigatorWithBattery;
    if (!nav.getBattery) {
      return { level: null, isCharging: null };
    }

    const battery = await nav.getBattery();
    return {
      level: Math.round(battery.level * 100),
      isCharging: battery.charging,
    };
  } catch {
    return { level: null, isCharging: null };
  }
}

/**
 * Run a quick benchmark to estimate CPU performance
 * Returns a score from 0-100
 */
async function runQuickBenchmark(): Promise<number> {
  return new Promise((resolve) => {
    const startTime = performance.now();

    // Simple math operations benchmark
    let result = 0;
    const iterations = 100000;

    for (let i = 0; i < iterations; i++) {
      result += Math.sqrt(i) * Math.sin(i) * Math.cos(i);
    }

    const endTime = performance.now();
    const duration = endTime - startTime;

    // Prevent unused variable warning
    void result;

    // Score calculation:
    // < 10ms = 100 (very fast)
    // 10-50ms = 70-99 (fast)
    // 50-100ms = 40-69 (medium)
    // 100-200ms = 20-39 (slow)
    // > 200ms = 0-19 (very slow)

    let score: number;
    if (duration < 10) {
      score = 100;
    } else if (duration < 50) {
      score = Math.round(100 - (duration - 10) * 0.75);
    } else if (duration < 100) {
      score = Math.round(70 - (duration - 50) * 0.6);
    } else if (duration < 200) {
      score = Math.round(40 - (duration - 100) * 0.2);
    } else {
      score = Math.max(0, Math.round(20 - (duration - 200) * 0.1));
    }

    resolve(score);
  });
}

/**
 * Calculate overall device tier
 */
function calculateDeviceTier(params: {
  cpuCores: number;
  memoryGB: number | null;
  gpuTier: DeviceCapability["gpuTier"];
  benchmarkScore: number;
}): DeviceTier {
  const { cpuCores, memoryGB, gpuTier, benchmarkScore } = params;

  // Point-based scoring system
  let points = 0;

  // CPU cores (max 30 points)
  if (cpuCores >= 8) points += 30;
  else if (cpuCores >= 6) points += 25;
  else if (cpuCores >= 4) points += 20;
  else if (cpuCores >= 2) points += 10;

  // Memory (max 25 points)
  if (memoryGB !== null) {
    if (memoryGB >= 8) points += 25;
    else if (memoryGB >= 4) points += 20;
    else if (memoryGB >= 2) points += 10;
  } else {
    // Default moderate score if unknown
    points += 12;
  }

  // GPU tier (max 25 points)
  if (gpuTier === "high") points += 25;
  else if (gpuTier === "medium") points += 15;
  else if (gpuTier === "low") points += 5;
  else points += 10; // unknown

  // Benchmark score (max 20 points)
  points += Math.round(benchmarkScore * 0.2);

  // Determine tier
  // 70-100 points = high
  // 40-69 points = medium
  // 0-39 points = low
  if (points >= 70) return "high";
  if (points >= 40) return "medium";
  return "low";
}

/**
 * Get recommended inference interval based on device tier
 */
function getInferenceInterval(tier: DeviceTier): number {
  switch (tier) {
    case "high":
      return 1000; // 1 second - more responsive
    case "medium":
      return 2000; // 2 seconds - balanced
    case "low":
      return 3000; // 3 seconds - power saving
  }
}

/**
 * Get recommended motion processing interval based on device tier
 * Motion detection is more CPU-intensive, so intervals are slightly longer
 */
function getMotionProcessingInterval(tier: DeviceTier): number {
  switch (tier) {
    case "high":
      return 500; // 500ms - responsive motion detection
    case "medium":
      return 750; // 750ms - balanced
    case "low":
      return 1000; // 1 second - power saving
  }
}

/**
 * Check if cry detection should run based on current conditions
 */
export function shouldEnableCryDetection(
  capability: DeviceCapability,
  userPreference: "auto" | "on" | "off"
): boolean {
  if (userPreference === "on") return true;
  if (userPreference === "off") return false;

  // Auto mode: use recommendation but also consider battery
  if (!capability.cryDetectionRecommended) return false;

  // If battery is low (< 20%) and not charging, disable
  if (
    capability.batteryLevel !== null &&
    capability.batteryLevel < 20 &&
    capability.isCharging === false
  ) {
    return false;
  }

  return true;
}

/**
 * Check if motion detection should run based on current conditions
 * Motion detection is more resource-intensive than cry detection
 */
export function shouldEnableMotionDetection(
  capability: DeviceCapability,
  userPreference: "auto" | "on" | "off"
): boolean {
  if (userPreference === "on") return true;
  if (userPreference === "off") return false;

  // Auto mode: use recommendation but also consider battery
  if (!capability.motionDetectionRecommended) return false;

  // If battery is low (< 30%) and not charging, disable
  // Motion detection uses more battery, so threshold is higher
  if (
    capability.batteryLevel !== null &&
    capability.batteryLevel < 30 &&
    capability.isCharging === false
  ) {
    return false;
  }

  return true;
}
